'use client'

import { useState } from 'react'
import Link from 'next/link'

import type { BlogsApiItem, TagApiItem } from '@/lib/api/blogs'
import { BgImageOverlay } from '@/components/ui/bg-image-overlay'
import { Col } from '@/components/ui/col'

type Props = {
  blogs: BlogsApiItem[]
  filterTags?: TagApiItem[]
  basePath: string
  filterAllLabel?: string
  readLabel: string
  loadMoreLabel: string
}

const GRID_INITIAL_VISIBLE = 5
const GRID_LOAD_MORE_STEP = 6
const FALLBACK_COVER_IMAGE = '/images/photo-1551288049-w1000.webp'

export function InsightsWhitepaperLibraryGrid({
  blogs,
  basePath,
  readLabel,
  loadMoreLabel,
}: Props) {
  const [visibleCount, setVisibleCount] = useState(GRID_INITIAL_VISIBLE)

  const filtered = blogs
  const visibleBlogs = filtered.slice(0, visibleCount)
  const canLoadMore = filtered.length > visibleCount

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {visibleBlogs.map((blog) => (
          <Link
            key={blog.documentId ?? String(blog.id)}
            href={`${basePath}/${blog.slug}`}
            className="group bg-white border border-gray-200 flex flex-col h-full rounded-lg shadow-sm hover:shadow-md hover:border-gray-400 hover:-translate-y-1 active:translate-y-0 active:bg-gray-50 transition-all duration-300 overflow-hidden"
          >
            <div className="aspect-[16/9] bg-charcoal-950 border-b border-gray-200 relative overflow-hidden flex items-center justify-center bg-grid-pattern-dark">
              <div className="w-full h-full border border-charcoal-300 shadow-md relative flex items-center justify-center editorial-cover rounded-sm overflow-hidden">
                <BgImageOverlay
                  src={blog.featured_image_url ?? FALLBACK_COVER_IMAGE}
                  fallbackSrc={FALLBACK_COVER_IMAGE}
                />
              </div>
            </div>
            <Col className="p-8 flex-grow">
              {blog.tags?.[0] && (
                <span className="text-fs-sm font-mono font-bold text-charcoal-500 uppercase tracking-widest block mb-3">
                  {blog.tags[0].name}
                </span>
              )}
              <h3 className="text-xl font-bold text-charcoal-900 leading-snug mb-4 group-hover:text-brand-red transition-colors line-clamp-3">
                {blog.title}
              </h3>
              {blog.short_description && (
                <p className="text-sm text-charcoal-600 font-light leading-relaxed mb-6 flex-grow line-clamp-4">
                  {blog.short_description}
                </p>
              )}
              <div className="flex items-center text-fs-sm font-bold text-charcoal-900 uppercase tracking-widest mt-auto border-t border-gray-100 pt-4 group-hover:text-brand-red transition-colors">
                <span>{readLabel}</span>
                <span className="ml-2 transform group-hover:translate-x-1.5 transition-transform duration-300">
                  &rarr;
                </span>
              </div>
            </Col>
          </Link>
        ))}
        {canLoadMore && (
          <button
            type="button"
            onClick={() =>
              setVisibleCount((current) =>
                Math.min(current + GRID_LOAD_MORE_STEP, filtered.length),
              )
            }
            className="flex w-full flex-col items-center justify-center bg-gray-100 border border-gray-200 min-h-[300px] cursor-pointer rounded-lg transition-colors hover:bg-gray-200 active:bg-gray-300 group"
          >
            <span className="w-10 h-10 border border-charcoal-400 rounded-full flex items-center justify-center mb-4 group-hover:border-brand-red transition-colors">
              <span className="w-2 h-2 bg-charcoal-400 rounded-full group-hover:bg-brand-red transition-colors" />
            </span>
            <span className="text-xs font-bold text-charcoal-600 uppercase tracking-widest group-hover:text-brand-red transition-colors">
              {loadMoreLabel}
            </span>
          </button>
        )}
      </div>
    </>
  )
}
